import React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactQuill from 'react-quill';
import { TextView, convertColor } from '../../components/Display';
import { postData } from '../../Api';

const EMPTY_REPLY = '{"type":"quill","document":{}}';

// Scoped styles: force RTL inside Quill and let message bubbles grow with their
// content instead of scrolling internally. Same approach as the support view.
const SCOPED_CSS = `
.taroff-task-view .ql-editor { direction: rtl; text-align: right; }
.taroff-task-view .composer .ql-editor { min-height: 110px; max-height: 220px; }
.taroff-task-view .ql-editor.ql-blank::before { left: auto; right: 15px; }
.taroff-task-view .bubble .ql-container { border: none; background: transparent; }
.taroff-task-view .bubble .ql-editor { padding: 0; min-height: 0; height: auto; overflow: visible; line-height: 1.9; }
`;

const PRIORITY_STYLE = {
    1: { label: 'اولویت بالا', className: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-950 dark:text-red-300 dark:border-red-800', icon: 'fas fa-arrow-up' },
    2: { label: 'اولویت عادی', className: 'bg-gray-50 text-gray-600 border-gray-200 dark:bg-zinc-800 dark:text-gray-300 dark:border-zinc-700', icon: 'fas fa-minus' },
    3: { label: 'اولویت پایین', className: 'bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-950 dark:text-sky-300 dark:border-sky-800', icon: 'fas fa-arrow-down' },
};

// Read-only render of a stored Quill value (JSON) or plain text, always RTL.
const RichContent = ({ value }) => {
    if (!value) return <span className="text-gray-400">—</span>;
    let doc = null;
    if (typeof value === 'string' && value.trim().startsWith('{')) {
        try {
            const parsed = JSON.parse(value);
            doc = parsed.document !== undefined ? parsed.document : parsed;
        } catch (e) {
            doc = null;
        }
    }
    if (doc !== null && typeof doc === 'object') {
        return <ReactQuill value={doc} readOnly={true} theme="bubble" modules={{ toolbar: false }} />;
    }
    return <div dir="rtl" className="whitespace-pre-wrap">{value}</div>;
};

const Badge = ({ color, children, icon }) => (
    <span
        className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium text-white"
        style={{ backgroundColor: convertColor(color) || '#6b7280' }}
    >
        {icon && <i className={icon} />}
        {children}
    </span>
);

const isEmptyReply = (text) => {
    try {
        const parsed = JSON.parse(text);
        return !parsed.document || !parsed.document.ops || parsed.document.ops.length === 0;
    } catch (e) {
        return !text || !text.trim();
    }
};

// One line of the activity log, rendered as a centred pill between messages.
const eventLabel = (event) => {
    const who = event.user_title || 'سیستم';
    if (event.action === 'created') return `${who} این کار را ایجاد کرد`;
    if (event.action === 'edited') return `${who} اطلاعات کار را ویرایش کرد`;
    if (event.action === 'assigned') {
        if (!event.new_value) return `${who} مسئول کار را برداشت`;
        return event.old_value
            ? `${who} مسئول را از ${event.old_value} به ${event.new_value} تغییر داد`
            : `${who} کار را به ${event.new_value} محول کرد`;
    }
    if (event.action === 'state') return `${who} وضعیت را به «${event.new_value}» تغییر داد`;
    return `${who}: ${event.action}`;
};

export default function TaroffTaskView(props) {
    const navigate = useNavigate();
    const id = props['task-id'];

    const [task, setTask] = useState(null);
    const [comments, setComments] = useState([]);
    const [events, setEvents] = useState([]);
    const [states, setStates] = useState([]);
    const [admins, setAdmins] = useState([]);
    const [me, setMe] = useState({});
    const [replyText, setReplyText] = useState(EMPTY_REPLY);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState(null);
    const threadEndRef = useRef(null);

    const loadData = async (scroll = false) => {
        try {
            const response = await postData('/api/panel/page/taroff-task-view/data', { id });
            if (response.status === 'success') {
                setTask(response.data.task);
                setComments(response.data.comments || []);
                setEvents(response.data.events || []);
                setStates(response.data.states || []);
                setAdmins(response.data.admins || []);
                setMe(response.data.me || {});
            } else {
                setError(response.message || 'خطا در بارگذاری اطلاعات کار');
            }
        } catch (e) {
            setError('خطا در برقراری ارتباط با سرور');
        } finally {
            setLoading(false);
            if (scroll) setTimeout(() => threadEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
        }
    };

    useEffect(() => {
        loadData(true);
    }, []);

    const handleSendReply = async () => {
        if (isEmptyReply(replyText) || sending) return;
        setSending(true);
        try {
            const response = await postData('/api/panel/page/taroff-task-comment/save', { id, text: replyText });
            if (response.status === 'success') {
                setReplyText(EMPTY_REPLY);
                await loadData(true);
            } else {
                setError(response.message || 'خطا در ثبت پاسخ');
            }
        } finally {
            setSending(false);
        }
    };

    const handleSetState = async (key) => {
        if (!task || task.state_id_key === key) return;
        const response = await postData('/api/panel/page/taroff-task-state-by-key/save', { id, key });
        if (response.status === 'success') {
            loadData();
        } else {
            setError(response.message || 'خطا در تغییر وضعیت');
        }
    };

    const handleAssign = async (value) => {
        const assignee_id = value ? parseInt(value) : null;
        const response = await postData('/api/panel/page/taroff-task-assign/save', { id, assignee_id });
        if (response.status === 'success') {
            loadData();
        } else {
            setError(response.message || 'خطا در تغییر مسئول');
        }
    };

    const handleCancel = () => {
        props.onDismiss ? props.onDismiss() : navigate(-1);
    };

    if (loading) {
        return (
            <div dir="rtl" className="flex items-center justify-center h-64 text-gray-500">
                <i className="fas fa-spinner fa-spin fa-lg ml-2" /> در حال بارگذاری...
            </div>
        );
    }

    if (error && !task) {
        return (
            <div dir="rtl" className="p-6 text-center">
                <div className="text-red-600 mb-4">{error}</div>
                <button onClick={handleCancel} className="px-4 py-2 rounded-lg bg-gray-100 dark:bg-zinc-700">بازگشت</button>
            </div>
        );
    }

    const priority = PRIORITY_STYLE[task.priority] || PRIORITY_STYLE[2];

    // The conversation: the original description first, then comments and the
    // activity log merged into a single time-ordered timeline.
    const timeline = [
        {
            kind: 'message',
            author: task.creator_title || 'ثبت‌کننده',
            mine: task.creator_id === me.id,
            time: task.insert_time,
            content: task.description,
            isRequest: true,
        },
        ...comments.map((c) => ({
            kind: 'message',
            author: c.user_title || 'کاربر',
            mine: c.user_id === me.id,
            time: c.insert_time,
            content: c.text,
        })),
        ...events
            .filter((e) => e.action !== 'created')
            .map((e) => ({ kind: 'event', time: e.insert_time, label: eventLabel(e) })),
    ].sort((a, b) => String(a.time).localeCompare(String(b.time)));

    return (
        <div dir="rtl" className="taroff-task-view flex flex-col h-[85vh] font-iransans text-gray-900 dark:text-gray-100">
            <style>{SCOPED_CSS}</style>

            {/* Header */}
            <div className="shrink-0 px-5 py-3.5 border-b border-gray-200 dark:border-zinc-700 flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <h2 className="text-base font-bold truncate">{task.title}</h2>
                    <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500">
                        <span className="font-mono">#{task.id}</span>
                        <span className="text-gray-300">|</span>
                        {task.type_id_title && <Badge color={task.type_id_color}>{task.type_id_title}</Badge>}
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium ${priority.className}`}>
                            <i className={`${priority.icon} text-[9px]`} />
                            {priority.label}
                        </span>
                        {task.shop_title && <Badge color={task.shop_color} icon="far fa-store">{task.shop_title}</Badge>}
                        <span className="text-gray-400">
                            <i className="far fa-user-pen ml-1" />
                            {task.creator_title || '—'}
                        </span>
                        <span className="text-gray-400"><TextView format={'persian-date-normal'}>{task.insert_time}</TextView></span>
                        {task.due_date && (
                            <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400">
                                <i className="far fa-calendar-clock" />
                                مهلت: <TextView format={'persian-date-normal'}>{task.due_date}</TextView>
                            </span>
                        )}
                    </div>
                </div>
                <button onClick={handleCancel} className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:bg-gray-100 dark:hover:bg-zinc-700 transition-colors" title="بستن">
                    <i className="fas fa-xmark fa-lg" />
                </button>
            </div>

            {/* Quick actions: state chips + assignee */}
            <div className="shrink-0 px-5 py-2.5 border-b border-gray-200 dark:border-zinc-700 bg-gray-50 dark:bg-zinc-900/60 flex flex-wrap items-center gap-2">
                <span className="text-xs text-gray-500 ml-1">وضعیت:</span>
                {states.map((s) => {
                    const active = task.state_id_key === s.key;
                    return (
                        <button
                            key={s.id}
                            onClick={() => handleSetState(s.key)}
                            className={`px-3 py-1 rounded-full text-xs font-medium border transition-all ${
                                active
                                    ? 'text-white border-transparent shadow-sm'
                                    : 'bg-white dark:bg-zinc-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-zinc-600 hover:border-gray-400'
                            }`}
                            style={active ? { backgroundColor: convertColor(s.color) || '#4f46e5' } : {}}
                        >
                            {active && <i className="fas fa-check ml-1 text-[10px]" />}
                            {s.title}
                        </button>
                    );
                })}
                <div className="flex items-center gap-1.5 mr-auto">
                    <span className="text-xs text-gray-500">مسئول:</span>
                    <select
                        value={task.assignee_id || ''}
                        onChange={(e) => handleAssign(e.target.value)}
                        className="text-xs rounded-lg border border-gray-200 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-2 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    >
                        <option value="">تعیین نشده</option>
                        {admins.map((a) => (
                            <option key={a.id} value={a.id}>{a.title}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Timeline */}
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 bg-gray-100/60 dark:bg-black/20">
                {timeline.map((item, index) => item.kind === 'event' ? (
                    <div key={index} className="flex justify-center">
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-white/70 dark:bg-zinc-800/70 border border-gray-200 dark:border-zinc-700 px-3 py-1 text-[11px] text-gray-500 dark:text-gray-400">
                            <i className="far fa-clock-rotate-left text-[10px]" />
                            {item.label}
                            <span className="text-gray-400"><TextView format={'persian-date-time'}>{item.time}</TextView></span>
                        </span>
                    </div>
                ) : (
                    <div key={index} className={`flex ${item.mine ? 'justify-start' : 'justify-end'}`}>
                        <div className={`bubble max-w-[82%] rounded-2xl px-4 py-2.5 border shadow-sm ${
                            item.mine
                                ? 'bg-indigo-50 border-indigo-100 dark:bg-indigo-950/40 dark:border-indigo-900/60'
                                : item.isRequest
                                    ? 'bg-amber-50 border-amber-100 dark:bg-amber-950/30 dark:border-amber-900/50'
                                    : 'bg-white border-gray-200 dark:bg-zinc-800 dark:border-zinc-700'
                        }`}>
                            <div className="flex items-center gap-2 mb-1">
                                <span className={`text-xs font-bold ${item.mine ? 'text-indigo-700 dark:text-indigo-300' : 'text-gray-700 dark:text-gray-300'}`}>
                                    <i className="far fa-user ml-1" />
                                    {item.author}
                                </span>
                                {item.isRequest && <span className="text-[10px] text-amber-600 dark:text-amber-400 font-medium">شرح کار</span>}
                                <span className="text-[11px] text-gray-400 mr-auto"><TextView format={'persian-date-time'}>{item.time}</TextView></span>
                            </div>
                            <div className="text-sm"><RichContent value={item.content} /></div>
                        </div>
                    </div>
                ))}
                <div ref={threadEndRef} />
            </div>

            {/* Composer */}
            <div className="shrink-0 border-t border-gray-200 dark:border-zinc-700 px-4 py-3 bg-white dark:bg-zinc-900">
                {error && <div className="text-red-600 text-xs mb-2">{error}</div>}
                <div className="composer">
                    <ReactQuill theme="snow" value={(() => { try { return JSON.parse(replyText).document; } catch (e) { return replyText; } })()}
                        onChange={(content, delta, source, editor) => setReplyText(JSON.stringify({ type: 'quill', document: editor.getContents() }))}
                        placeholder="پیام خود را بنویسید..." />
                </div>
                <div className="mt-2.5 flex items-center justify-between">
                    <span className="text-xs text-gray-400">
                        {task.state_is_final ? 'این کار بسته شده است' : ''}
                    </span>
                    <button
                        onClick={handleSendReply}
                        disabled={isEmptyReply(replyText) || sending}
                        className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                        <i className={sending ? 'fas fa-spinner fa-spin' : 'fas fa-paper-plane'} />
                        ثبت پیام
                    </button>
                </div>
            </div>
        </div>
    );
}
